/* =========================================================
   Search Suggestions panel
   - Opens under the theme's own search box when it is focused
     and empty. Hides as soon as the shopper types, so the
     theme's own live search results keep working.
   - Does not replace or edit any theme script.
   ========================================================= */
(function () {
  'use strict';

  if (window.VtSearchSuggestions) {
    window.VtSearchSuggestions.init();
    return;
  }

  var MOBILE_BP = 750;
  var instances = {};

  function safeMatches(el, selector) {
    try { return el.matches(selector); } catch (e) { return false; }
  }

  function safeClosest(el, selector) {
    try { return el.closest(selector); } catch (e) { return null; }
  }

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.closest('[inert]')) return false;
    var r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return false;
    if (r.bottom <= 0 || r.top >= window.innerHeight) return false;
    var cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    var op = parseFloat(cs.opacity);
    return isNaN(op) || op > 0.05;
  }

  function throttleFrame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  /* ---------------- Slider ---------------- */
  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-vss-track]');
    this.prev = root.querySelector('[data-vss-prev]');
    this.next = root.querySelector('[data-vss-next]');
    if (!this.track) return;

    var self = this;
    this.update = this.update.bind(this);
    this.onScroll = throttleFrame(this.update);

    if (this.prev) {
      this.prev.addEventListener('click', function (e) {
        e.preventDefault();
        self.go(-1);
      });
    }
    if (this.next) {
      this.next.addEventListener('click', function (e) {
        e.preventDefault();
        self.go(1);
      });
    }
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Keep arrows centred on the image once lazy images load
    this.track.addEventListener('load', this.onScroll, true);
  }

  Slider.prototype.step = function () {
    var track = this.track;
    if (this.root.getAttribute('data-scroll-by') === 'item') {
      var slide = track.querySelector('.vss-slide');
      if (slide) {
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      }
    }
    return track.clientWidth;
  };

  Slider.prototype.go = function (dir) {
    this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
  };

  Slider.prototype.update = function () {
    var track = this.track;
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth;
    var atStart = track.scrollLeft <= 2;
    var atEnd = track.scrollLeft >= max - 2;

    this.root.classList.toggle('vss-slider--static', max <= 2);
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;

    var media = track.querySelector('.vss-card__media');
    if (media && media.offsetHeight) {
      this.root.style.setProperty('--vss-arrow-top', media.offsetHeight / 2 + 'px');
    }
  };

  /* ---------------- Panel ---------------- */
  function SearchSuggest(panel) {
    var d = panel.dataset;
    this.panel = panel;
    this.id = d.sectionId;
    this.inputSel = (d.inputSelector || '').trim() || 'input[name="q"]';
    this.anchorSel = (d.anchorSelector || '').trim() || 'form';
    this.widthMode = d.widthMode || 'match';
    this.minW = parseInt(d.minWidth, 10) || 700;
    this.maxW = parseInt(d.maxWidth, 10) || 1400;
    this.offset = parseInt(d.offset, 10) || 0;
    this.mobileMargin = parseInt(d.mobileMargin, 10);
    if (isNaN(this.mobileMargin)) this.mobileMargin = 8;
    this.includeMain = d.includeMain === 'true';

    this.input = null;
    this.isOpen = false;
    this.preview = false;
    this.seenVisible = false;
    this.timer = null;
    this.hideTimer = null;

    // Move the panel to <body> so header styles can't clip it
    if (panel.parentElement !== document.body) {
      this.home = panel.parentElement;
      document.body.appendChild(panel);
    }

    this.sliders = [];
    var sliderEls = panel.querySelectorAll('[data-vss-slider]');
    for (var i = 0; i < sliderEls.length; i++) {
      this.sliders.push(new Slider(sliderEls[i]));
    }

    this.bind();
  }

  SearchSuggest.prototype.isTarget = function (el) {
    if (!el || el.tagName !== 'INPUT') return false;
    if (!safeMatches(el, this.inputSel)) return false;
    if (this.panel.contains(el)) return false;
    if (!this.includeMain && el.closest('main, #MainContent, [role="main"]')) return false;
    return true;
  };

  SearchSuggest.prototype.bind = function () {
    var self = this;

    this.onFocusIn = function (e) {
      var t = e.target;
      if (!self.isTarget(t)) return;
      self.input = t;
      if (!t.value.trim()) self.open();
    };

    this.onInput = function (e) {
      var t = e.target;
      if (!self.isTarget(t)) return;
      self.input = t;
      if (t.value.trim()) {
        self.close();
      } else if (document.activeElement === t) {
        self.open();
      }
    };

    this.onClick = function (e) {
      var t = e.target;
      if (self.isTarget(t) && !t.value.trim()) {
        self.input = t;
        self.open();
      }
    };

    this.onPointerDown = function (e) {
      if (!self.isOpen || self.preview) return;
      var t = e.target;
      if (self.panel.contains(t)) return;
      if (self.input && (t === self.input)) return;
      var anchor = self.input ? safeClosest(self.input, self.anchorSel) : null;
      if (anchor && anchor.contains(t)) return;
      self.close();
    };

    this.onKeyDown = function (e) {
      if (self.isOpen && (e.key === 'Escape' || e.key === 'Esc')) self.close();
    };

    this.onViewport = throttleFrame(function () {
      if (self.isOpen) {
        self.position();
        self.refreshSliders();
      }
    });

    document.addEventListener('focusin', this.onFocusIn, true);
    document.addEventListener('input', this.onInput, true);
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('pointerdown', this.onPointerDown, true);
    document.addEventListener('keydown', this.onKeyDown, true);

    // Clicks inside the panel must not reach theme "click outside"
    // handlers (which would close the theme's search box).
    this.stop = function (e) { e.stopPropagation(); };
    ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart', 'touchend', 'focusin', 'focusout'].forEach(function (type) {
      self.panel.addEventListener(type, self.stop, { passive: true });
    });

    // Keep focus in the search box when clicking panel content
    this.keepFocus = function (e) {
      if (e.button !== 0) return;
      if (self.preview) return;
      e.preventDefault();
    };
    this.panel.addEventListener('mousedown', this.keepFocus);
  };

  SearchSuggest.prototype.unbind = function () {
    var self = this;
    document.removeEventListener('focusin', this.onFocusIn, true);
    document.removeEventListener('input', this.onInput, true);
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart', 'touchend', 'focusin', 'focusout'].forEach(function (type) {
      self.panel.removeEventListener(type, self.stop, { passive: true });
    });
    this.panel.removeEventListener('mousedown', this.keepFocus);
  };

  SearchSuggest.prototype.open = function () {
    var self = this;
    Object.keys(instances).forEach(function (key) {
      if (instances[key] !== self) instances[key].close();
    });

    if (this.isOpen) {
      this.tick();
      return;
    }

    this.isOpen = true;
    this.seenVisible = false;
    this.openedAt = Date.now();
    clearTimeout(this.hideTimer);
    this.panel.hidden = false;

    this.tick();
    this.timer = setInterval(function () { self.tick(); }, 150);

    window.addEventListener('resize', this.onViewport, { passive: true });
    window.addEventListener('scroll', this.onViewport, { passive: true, capture: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewport, { passive: true });
    }
  };

  SearchSuggest.prototype.tick = function () {
    if (!this.isOpen) return;

    if (this.preview) {
      this.position();
      this.show();
      return;
    }

    if (this.input && isVisible(this.input)) {
      this.seenVisible = true;
      this.position();
      this.show();
    } else if (this.seenVisible || Date.now() - this.openedAt > 2000) {
      // The theme closed its search box
      this.close();
    } else {
      // Search box still animating in
      this.panel.classList.remove('is-visible');
    }
  };

  SearchSuggest.prototype.show = function () {
    if (this.panel.classList.contains('is-visible')) return;
    this.panel.classList.add('is-visible');
    this.refreshSliders();
  };

  SearchSuggest.prototype.refreshSliders = function () {
    for (var i = 0; i < this.sliders.length; i++) this.sliders[i].update();
  };

  SearchSuggest.prototype.close = function () {
    if (!this.isOpen) return;
    var self = this;
    this.isOpen = false;
    this.preview = false;
    clearInterval(this.timer);
    this.timer = null;

    window.removeEventListener('resize', this.onViewport, { passive: true });
    window.removeEventListener('scroll', this.onViewport, { passive: true, capture: true });
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewport, { passive: true });
    }

    this.panel.classList.remove('is-visible');
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(function () {
      if (!self.isOpen) self.panel.hidden = true;
    }, 220);
  };

  SearchSuggest.prototype.position = function () {
    var docW = document.documentElement.clientWidth || window.innerWidth;
    var viewH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var mobile = docW < MOBILE_BP;
    var margin = mobile ? this.mobileMargin : 16;
    var avail = Math.max(docW - margin * 2, 200);
    var rect;

    if (this.input && isVisible(this.input)) {
      var anchor = safeClosest(this.input, this.anchorSel) || this.input;
      rect = anchor.getBoundingClientRect();
      if (rect.width < 2) rect = this.input.getBoundingClientRect();
    } else {
      // Theme editor preview with no visible search box
      rect = { left: (docW - Math.min(this.maxW, avail)) / 2, width: Math.min(this.maxW, avail), bottom: 90 };
    }

    var width, left;
    if (mobile || this.widthMode === 'full') {
      width = avail;
      left = (docW - width) / 2;
    } else if (this.widthMode === 'center') {
      width = Math.min(this.maxW, avail);
      left = (docW - width) / 2;
    } else {
      width = Math.min(Math.max(rect.width, this.minW), this.maxW, avail);
      left = Math.min(Math.max(rect.left, margin), docW - width - margin);
    }

    var top = Math.max(rect.bottom + this.offset, 0);
    var maxH = Math.max(viewH - top - 12, 220);

    var st = this.panel.style;
    st.left = Math.round(left) + 'px';
    st.top = Math.round(top) + 'px';
    st.width = Math.round(width) + 'px';
    st.maxHeight = Math.round(maxH) + 'px';
  };

  SearchSuggest.prototype.firstVisibleInput = function () {
    var list;
    try { list = document.querySelectorAll(this.inputSel); } catch (e) { return null; }
    for (var i = 0; i < list.length; i++) {
      if (this.isTarget(list[i]) && isVisible(list[i])) return list[i];
    }
    return null;
  };

  /* Theme editor: show the panel while the section is selected */
  SearchSuggest.prototype.showPreview = function () {
    this.input = this.firstVisibleInput();
    this.preview = true;
    if (this.isOpen) {
      this.tick();
    } else {
      this.open();
      this.preview = true;
      this.tick();
    }
  };

  SearchSuggest.prototype.destroy = function () {
    this.close();
    clearTimeout(this.hideTimer);
    this.unbind();
    if (this.panel && this.panel.parentNode) this.panel.parentNode.removeChild(this.panel);
    delete instances[this.id];
  };

  /* ---------------- Boot ---------------- */
  function create(panel) {
    var id = panel.getAttribute('data-section-id');
    if (!id) return;
    if (instances[id] && instances[id].panel !== panel) instances[id].destroy();
    if (instances[id]) return;
    panel.setAttribute('data-vss-ready', 'true');
    instances[id] = new SearchSuggest(panel);
  }

  function init() {
    var panels = document.querySelectorAll('[data-vss-panel]:not([data-vss-ready])');
    for (var i = 0; i < panels.length; i++) create(panels[i]);
  }

  window.VtSearchSuggestions = { init: init, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor events */
  document.addEventListener('shopify:section:load', function (e) {
    var panel = e.target && e.target.querySelector ? e.target.querySelector('[data-vss-panel]') : null;
    if (panel) create(panel);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) instances[id].destroy();
  });

  function previewFor(e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) instances[id].showPreview();
  }

  function closeFor(e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) instances[id].close();
  }

  document.addEventListener('shopify:section:select', previewFor);
  document.addEventListener('shopify:block:select', function (e) {
    previewFor(e);
    var id = e.detail && e.detail.sectionId;
    var blockEl = e.target;
    if (id && instances[id] && blockEl && instances[id].panel.contains(blockEl)) {
      blockEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  });
  document.addEventListener('shopify:section:deselect', closeFor);
})();
